import {
	ColumnDef,
	PaginationState,
	SortingState,
	flexRender,
	getCoreRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useState } from "react";

import {
	ArrowsUpDownIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useInView } from "react-intersection-observer";
import {
	Button,
	ButtonProps,
	Pagination,
	PaginationContent,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from ".";
import { cn } from "../../lib/utils";

interface DataTableProps<TData, TValue> {
	columns: ColumnDef<TData, TValue>[];
	data: TData[];
	fetchNextPage: () => void;
	hasNextPage: boolean;
	onRowClick?: (row?: TData) => void;
	emptyDataText?: string;
	nonInteractive?: boolean;
}

const DataTable = <TData, TValue>({
	columns,
	data,
	fetchNextPage,
	onRowClick = () => {},
	emptyDataText = "No results found",
	hasNextPage,
	nonInteractive = false,
}: DataTableProps<TData, TValue>) => {
	const { ref, inView } = useInView();

	const [sorting, setSorting] = React.useState<SortingState>([]);

	const table = useReactTable({
		data,
		columns,
		state: {
			sorting,
		},
		getCoreRowModel: getCoreRowModel(),
		onSortingChange: setSorting,
		getSortedRowModel: getSortedRowModel(),
	});

	useEffect(() => {
		if (inView && hasNextPage) {
			fetchNextPage();
		}
	}, [inView]);

	return (
		<div className="border-gray-100 border rounded-lg overflow-hidden">
			<Table>
				<TableHeader className="bg-gray-100">
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRow nonInteractive={nonInteractive} key={headerGroup.id}>
							{headerGroup.headers.map((header) => {
								if (header.isPlaceholder) return null;
								return (
									<TableHead
										key={header.id}
										className={cn(
											"text-xs",
											{
												"cursor-pointer select-none hover:text-alta-light-green":
													header.column.getCanSort(),
											},
											{ "text-alta-light-green": header.column.getIsSorted() },
										)}
										onClick={header.column.getToggleSortingHandler()}
									>
										<div
											className={cn(
												"flex items-center space-x-1",
												{
													"justify-end":
														header.column.columnDef.meta?.align === "right",
												},
												{
													"justify-center":
														header.column.columnDef.meta?.align === "center",
												},
											)}
										>
											<span>
												{flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
											</span>
											{header.column.getCanSort() && (
												<span>
													{header.column.getIsSorted() ? (
														header.column.getIsSorted() === "desc" ? (
															<span> ↓</span>
														) : (
															<span> ↑</span>
														)
													) : (
														<ArrowsUpDownIcon className="h-3 w-3" />
													)}
												</span>
											)}
										</div>
									</TableHead>
								);
							})}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{table.getRowModel().rows?.length ? (
						<>
							{table.getRowModel().rows.map((row) => (
								<TableRow
									nonInteractive={nonInteractive}
									onClick={
										nonInteractive
											? undefined
											: (event) => {
													if ((event.target as HTMLElement).id === row.id) {
														onRowClick(row.original);
													}
												}
									}
									key={row.id}
									data-state={row.getIsSelected() && "selected"}
								>
									{row.getVisibleCells().map((cell) => (
										<TableCell
											id={row.id}
											key={cell.id}
											align={cell.column.columnDef.meta?.align}
										>
											<div
												id={row.id}
												className={cn(
													`w-[${cell.column.getSize()}px] truncate`,
													{
														"min-w-[80px]":
															!cell.column.columnDef.meta?.noMinWidth,
													},
												)}
											>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</div>
										</TableCell>
									))}
								</TableRow>
							))}
							<tr ref={ref} />
						</>
					) : (
						<TableRow nonInteractive={nonInteractive}>
							<TableCell colSpan={columns.length} className="h-24 text-center">
								{emptyDataText}
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</div>
	);
};

interface PaginatedDataTableProps<TData, TValue> {
	columns: ColumnDef<TData, TValue>[];
	data: TData[];
	onRowClick?: (row?: TData) => void;
	initialSorting?: SortingState;
	emptyDataText?: string;
	defaultPageSize?: number;
	pageSizeOptions?: number[];
	nonInteractive?: boolean;
	variant?: "default" | "flat";
}

/**
 *
 * Client-side paginated data table, default pageSize = 20
 *
 */
const PaginatedDataTable = <TData, TValue>({
	columns,
	data,
	onRowClick = () => {},
	emptyDataText = "No results found",
	initialSorting = [],
	defaultPageSize = 20,
	pageSizeOptions = [10, 20, 30, 40, 50],
	nonInteractive = false,
	variant = "default",
}: PaginatedDataTableProps<TData, TValue>) => {
	const [sorting, setSorting] = React.useState<SortingState>(initialSorting);
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: defaultPageSize,
	});

	const pageCount = Math.ceil(data.length / pagination.pageSize);
	const rowCount = data.length;

	const table = useReactTable({
		data,
		columns,
		state: {
			sorting,
			pagination,
		},
		getCoreRowModel: getCoreRowModel(),
		onSortingChange: setSorting,
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		onPaginationChange: setPagination,
	});

	const renderPaginationLabel = () => {
		if (data.length === 0) {
			return (
				<span className="flex items-center">
					<strong>0&nbsp;-&nbsp;0</strong>
					&nbsp;of&nbsp;
					<strong>0</strong>
				</span>
			);
		}
		return (
			<span className="flex items-center">
				<strong>
					{table.getState().pagination.pageIndex * pagination.pageSize + 1}
					&nbsp;-&nbsp;
					{table.getState().pagination.pageIndex * pagination.pageSize +
						table.getRowModel().rows.length}
				</strong>
				&nbsp;of&nbsp;
				<strong>{rowCount}</strong>
			</span>
		);
	};

	const renderPaginationControls = () => {
		// Inspired by https://mui.com/material-ui/react-pagination/
		let content = null;
		if (pageCount < 8) {
			// Scenario 1:
			// < 1 2 3 4 5 6 7 >
			// total < 8
			content = (
				<>
					{new Array(pageCount).fill(0).map((_, index) => (
						<PaginationButton
							variant="secondary"
							key={index}
							isActive={index === pagination.pageIndex}
							onClick={() => {
								setPagination({
									...pagination,
									pageIndex: index,
								});
							}}
						>
							{index + 1}
						</PaginationButton>
					))}
				</>
			);
		} else if (pagination.pageIndex < 4) {
			// Scenario 2:
			// < 1 2 3 4 5 ... 10 >
			// (pageIndex - startIndex) < 4
			content = (
				<>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === 1}
						onClick={() => setPagination({ ...pagination, pageIndex: 0 })}
					>
						1
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === 2}
						onClick={() => setPagination({ ...pagination, pageIndex: 1 })}
					>
						2
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === 3}
						onClick={() => setPagination({ ...pagination, pageIndex: 2 })}
					>
						3
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === 4}
						onClick={() => setPagination({ ...pagination, pageIndex: 3 })}
					>
						4
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === 5}
						onClick={() => setPagination({ ...pagination, pageIndex: 4 })}
					>
						5
					</PaginationButton>
					<span>...</span>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === pageCount - 1}
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pageCount - 1 })
						}
					>
						{pageCount}
					</PaginationButton>
				</>
			);
		} else if (pageCount - pagination.pageIndex < 4) {
			// Scenario 3:
			// < 1 ... 6 7 8 9 10 >
			// (endIndex - pageIndex) < 4
			content = (
				<>
					<PaginationButton
						isActive={false}
						variant="secondary"
						onClick={() => setPagination({ ...pagination, pageIndex: 0 })}
					>
						1
					</PaginationButton>
					<span>...</span>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === pageCount - 4}
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pageCount - 5 })
						}
					>
						{pageCount - 4}
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === pageCount - 3}
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pageCount - 4 })
						}
					>
						{pageCount - 3}
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === pageCount - 2}
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pageCount - 3 })
						}
					>
						{pageCount - 2}
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === pageCount - 1}
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pageCount - 2 })
						}
					>
						{pageCount - 1}
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={pagination.pageIndex + 1 === pageCount}
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pageCount - 1 })
						}
					>
						{pageCount}
					</PaginationButton>
				</>
			);
		} else {
			// Scenario 4:
			// < 1 ... 4 5 6 ... 10 >
			content = (
				<>
					<PaginationButton
						variant="secondary"
						isActive={false}
						onClick={() => setPagination({ ...pagination, pageIndex: 1 })}
					>
						1
					</PaginationButton>
					<span>...</span>
					<PaginationButton
						variant="secondary"
						isActive={false}
						onClick={() =>
							setPagination({
								...pagination,
								pageIndex: pagination.pageIndex - 1,
							})
						}
					>
						{pagination.pageIndex}
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={true}
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pagination.pageIndex })
						}
					>
						{pagination.pageIndex + 1}
					</PaginationButton>
					<PaginationButton
						variant="secondary"
						isActive={false}
						onClick={() =>
							setPagination({
								...pagination,
								pageIndex: pagination.pageIndex + 1,
							})
						}
					>
						{pagination.pageIndex + 2}
					</PaginationButton>
					<span>...</span>
					<PaginationButton
						variant="secondary"
						isActive={false}
						onClick={() =>
							setPagination({ ...pagination, pageIndex: pageCount - 1 })
						}
					>
						{pageCount}
					</PaginationButton>
				</>
			);
		}

		return (
			<PaginationContent className="flex items-center space-x-2">
				{/* <div className=""> */}
				<PaginationButton
					variant="secondary"
					className={cn("p-3", {
						"disabled:bg-transparent disabled:text-slate-400":
							!table.getCanPreviousPage(),
					})}
					onClick={() => table.previousPage()}
					isActive={false}
					disabled={!table.getCanPreviousPage()}
				>
					<ChevronLeftIcon className="size-4" />
				</PaginationButton>
				{content}
				<PaginationButton
					variant="secondary"
					className={cn("p-3", {
						"disabled:bg-transparent disabled:text-slate-400":
							!table.getCanNextPage(),
					})}
					onClick={() => table.nextPage()}
					isActive={false}
					disabled={!table.getCanNextPage()}
				>
					<ChevronRightIcon className="size-4" />
				</PaginationButton>
			</PaginationContent>
		);
	};

	return (
		<div
			className={cn(
				"overflow-hidden",
				variant === "default" && "border border-gray-100 rounded-lg",
				variant === "flat" && "border-0 rounded-none",
			)}
		>
			<Table>
				<TableHeader
					className={cn(
						variant === "default" && "bg-gray-100",
						variant === "flat" && "bg-transparent",
					)}
				>
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRow nonInteractive={nonInteractive} key={headerGroup.id}>
							{headerGroup.headers.map((header) => {
								if (header.isPlaceholder) return null;
								return (
									<TableHead
										key={header.id}
										className={cn(
											"text-xs",
											{
												"cursor-pointer select-none hover:text-alta-light-green":
													header.column.getCanSort(),
											},
											{ "text-alta-light-green": header.column.getIsSorted() },
										)}
										onClick={header.column.getToggleSortingHandler()}
									>
										<div
											className={cn(
												"flex items-center space-x-1",
												{
													"justify-end":
														header.column.columnDef.meta?.align === "right",
												},
												{
													"justify-center":
														header.column.columnDef.meta?.align === "center",
												},
											)}
										>
											<span>
												{flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
											</span>
											{header.column.getCanSort() && (
												<span>
													{header.column.getIsSorted() ? (
														header.column.getIsSorted() === "desc" ? (
															<span> ↓</span>
														) : (
															<span> ↑</span>
														)
													) : (
														<ArrowsUpDownIcon className="h-3 w-3" />
													)}
												</span>
											)}
										</div>
									</TableHead>
								);
							})}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{table.getRowModel().rows?.length ? (
						<>
							{table.getRowModel().rows.map((row, index) => (
								<TableRow
									nonInteractive={nonInteractive}
									onClick={
										nonInteractive
											? undefined
											: (event) => {
													if (
														(event.target as HTMLElement).id === row.id ||
														(event.target as HTMLElement).getElementsByTagName(
															"div",
														).length > 0
													) {
														onRowClick(row.original);
													}
												}
									}
									key={row.id}
									data-state={row.getIsSelected() && "selected"}
									className={cn(
										variant === "flat"
											? index === table.getRowModel().rows.length - 1
												? "border-b border-gray-100"
												: "border-none"
											: "",
									)}
								>
									{row.getVisibleCells().map((cell) => (
										<TableCell
											key={cell.id}
											align={cell.column.columnDef.meta?.align}
											className={cn(
												variant === "flat" ? "pt-5 pb-6 border-0" : "py-2",
												cell.column.columnDef.meta?.className,
											)}
										>
											<div
												id={row.id}
												className={cn(
													`w-[${cell.column.getSize()}px] truncate`,
													{
														"min-w-[80px]":
															!cell.column.columnDef.meta?.noMinWidth,
													},
												)}
											>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</div>
										</TableCell>
									))}
								</TableRow>
							))}
						</>
					) : (
						<TableRow nonInteractive={nonInteractive}>
							<TableCell colSpan={columns.length} className="py-4 text-center">
								{emptyDataText}
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>

			<Pagination
				className={cn(
					"justify-end space-x-4 px-4 py-2 text-sm",
					variant === "default" && "bg-slate-50",
					variant === "flat" && "bg-transparent border-t border-gray-100",
				)}
			>
				{renderPaginationLabel()}
				{renderPaginationControls()}
				<select
					value={table.getState().pagination.pageSize}
					className="rounded-lg bg-transparent px-2 hover:bg-slate-200"
					onChange={(e) => {
						table.setPageSize(Number(e.target.value));
					}}
				>
					{pageSizeOptions.map((pageSize) => (
						<option key={pageSize} value={pageSize}>
							Show {pageSize}
						</option>
					))}
				</select>
			</Pagination>
		</div>
	);
};

interface PaginationButtonProps extends ButtonProps {
	isActive: boolean;
}

function PaginationButton(props: PaginationButtonProps) {
	const { isActive = false, className, ...rest } = props;
	return (
		<Button
			type="button"
			{...rest}
			className={cn(
				"hover:bg-slate-200 bg-transparent rounded-full",
				{
					"bg-slate-200": isActive,
				},
				className,
			)}
		/>
	);
}

export { DataTable, PaginatedDataTable };
